/**
 * Weekly bucket drill-down — the bridge between a weekly bar and the existing day drill-down.
 *
 * `OverviewDrilldownModal` is keyed to ONE Central calendar day: it turns `dayIso` into the exact
 * half-open UTC window the bar counted (`tzDayRangeUtc`). A weekly bar covers up to seven of those
 * windows, so rather than inventing a second, week-shaped SQL path that could disagree with the
 * chart, clicking a week opens this picker: the week's days with the counts the chart itself
 * summed, and picking one hands off to the day drill-down that already exists.
 *
 * HomeScreen skips this entirely when a week has exactly one day with activity — that click goes
 * straight to the day, so the common case is still one click to rows.
 *
 * Reads nothing. Every number shown was computed by the chart, not re-queried.
 */
import { DAY_TZ, type DaySeriesKind } from './OverviewDrilldownModal';
import { useEscToClose } from '../lib/useEscToClose';
import { EmptyState } from '../ui/EmptyState';

/** One Central calendar day inside the clicked week, with the count its bar contributed. */
export type WeekPickerDay = { iso: string; label: string; value: number };

export type WeekDrilldownTarget = {
  kind: DaySeriesKind;
  /** The chart's own x-axis label for the bucket ("Jul 20–26"). */
  weekLabel: string;
  /** Sum of the days below — the height of the bar that was clicked. */
  total: number;
  /** Cumulative total at the end of the week when a cumulative presentation was clicked. */
  runningTotal: number | null;
  /** The bucket covers fewer than 7 days (window edge or the in-progress week). */
  partial: boolean;
  days: WeekPickerDay[];
};

const TITLES: Record<DaySeriesKind, string> = {
  signups: 'New users',
  ratings: 'Ratings',
  venues: 'Venues rated for the first time',
  raters: 'Distinct raters',
  'new-raters': 'First-time raters',
};

const NOUNS: Record<DaySeriesKind, [string, string]> = {
  signups: ['new user', 'new users'],
  ratings: ['rating', 'ratings'],
  venues: ['venue rated for the first time', 'venues rated for the first time'],
  raters: ['distinct rater', 'distinct raters'],
  'new-raters': ['first-time rater', 'first-time raters'],
};

function noun(kind: DaySeriesKind, n: number): string {
  const [one, many] = NOUNS[kind];
  return n === 1 ? one : many;
}

export function WeekDrilldownPicker({
  target,
  onPickDay,
  onClose,
}: {
  target: WeekDrilldownTarget | null;
  onPickDay: (day: WeekPickerDay) => void;
  onClose: () => void;
}) {
  useEscToClose(Boolean(target), onClose);

  if (!target) return null;

  const active = target.days.filter((d) => d.value > 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={`${TITLES[target.kind]} · week of ${target.weekLabel}`}
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-border bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="admin-label-primary truncate text-base">
              {TITLES[target.kind]} · {target.weekLabel}
            </h2>
            <div className="mt-1 text-xs text-mutedDim">
              {target.total.toLocaleString()} {noun(target.kind, target.total)} this week
              {target.runningTotal == null ? '' : ` · running total ${target.runningTotal.toLocaleString()}`}
              {target.partial ? ' · partial week' : ''}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted transition-colors hover:bg-white/[0.04] hover:text-white"
          >
            Close
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          <p className="mb-3 text-[11px] leading-relaxed text-mutedDim">
            Weeks run Monday–Sunday and are built from the same midnight-{DAY_TZ} day buckets as the
            daily chart. Pick a day to list the rows behind it.
          </p>
          {active.length === 0 ? (
            <EmptyState
              compact
              title="Nothing landed in this week"
              hint="Every day in this bucket counted zero, so there are no rows to list."
            />
          ) : (
            <ul className="space-y-1.5">
              {target.days.map((d) => {
                const clickable = d.value > 0;
                const inner = (
                  <>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{d.label}</span>
                    <span className="shrink-0 text-[11px] tabular-nums text-mutedDim">{d.iso}</span>
                    <span
                      className={`w-24 shrink-0 text-right text-[11px] tabular-nums ${
                        clickable ? 'text-muted' : 'text-mutedDim'
                      }`}
                    >
                      {d.value.toLocaleString()} {noun(target.kind, d.value)}
                    </span>
                  </>
                );
                const cls =
                  'flex w-full items-center gap-3 rounded-lg border border-border bg-rowAlt px-3 py-2 text-left';
                return (
                  <li key={d.iso}>
                    {clickable ? (
                      <button
                        type="button"
                        onClick={() => onPickDay(d)}
                        className={`${cls} text-violet-300/95 transition-colors hover:bg-white/[0.04] hover:text-violet-200`}
                      >
                        {inner}
                      </button>
                    ) : (
                      <div className={`${cls} text-mutedDim`}>{inner}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
